import { type AriaToolbarProps, useToolbar } from '@react-aria/toolbar';
import clsx from 'clsx';
import * as React from 'react';

import { Button, type ButtonProps } from '../Button';

/**
 * Toolbar implementing the WAI-ARIA Authoring Practices Toolbar pattern.
 */
export type ToolbarProps = React.HTMLAttributes<HTMLDivElement> &
    AriaToolbarProps & {
        /** Option to remove toolbar's default styles */
        unstyled?: boolean;
        /** Override size of all buttons in the toolbar */
        size?: ButtonProps['size'];
    };
export type ToolbarRef = {
    focus: () => void;
};

export function Toolbar({
    ref: forwardedRef,
    ...props
}: ToolbarProps & { ref?: React.ForwardedRef<ToolbarRef> }) {
    const { size, unstyled, ...domProps } = props;
    const ref = React.useRef<HTMLDivElement>(null);
    const { toolbarProps } = useToolbar(domProps, ref);

    React.useImperativeHandle(
        forwardedRef,
        () => ({
            focus: () => {
                if (!ref.current) {
                    return;
                }
                // Focus the first child in the toolbar
                const element = Array.from(ref.current.children).find(
                    (child): child is HTMLElement => child instanceof HTMLElement
                );
                element?.focus();
            },
        }),
        []
    );

    return (
        <div
            ref={ref}
            {...domProps}
            {...toolbarProps}
            role="toolbar"
            aria-label={props['aria-label']}
            aria-orientation={props.orientation}
            onKeyDownCapture={(event) => {
                props.onKeyDownCapture?.(event);
                if (!event.isDefaultPrevented()) {
                    toolbarProps.onKeyDownCapture?.(event);
                }
            }}
            className={clsx(
                'pointer-events-auto flex w-fit shrink-0 [&>button]:shrink-0',
                !unstyled &&
                    'gap-1 rounded-md bg-base p-0.5 shadow-overlay dark:border dark:border-neutral-subtle',
                props.orientation === 'vertical' && 'flex-col',
                props.className
            )}
        >
            {renderToolbarChildren(props.children, { size }, true)}
        </div>
    );
}

/**
 * Wrapper to group toolbar items together (separated by a border)
 */
export type ToolbarGroupProps = React.PropsWithChildren<
    React.HTMLAttributes<HTMLDivElement> & {
        /*
         * A label to define a group of related tools
         * For example, label='alignment' for a set of buttons
         * with RightAlign, LeftAlign, CenterAlign options
         */
        'aria-label'?: string;
        /** Size of the buttons in the toolbar */
        size?: ButtonProps['size'];
        /** Disabling all buttons in a group */
        disabled?: boolean;
    }
>;

export function ToolbarGroup(props: ToolbarGroupProps) {
    const { className, disabled, size, ...domProps } = props;
    return (
        <div
            role="group"
            aria-label={domProps['aria-label']}
            className={clsx(
                'peer relative flex gap-1 [&>button]:shrink-0',
                // Border to separate groups, except on first group
                // Needs specific styles to take less height than toolbar height
                '[&:not(:first-child)]:pl-0.5 [&:not(:first-child)]:before:pointer-events-none [&:not(:first-child)]:before:absolute [&:not(:first-child)]:before:inset-y-1 [&:not(:first-child)]:before:-left-0.5 [&:not(:first-child)]:before:w-px [&:not(:first-child)]:before:bg-[var(--sp-color-border-base)] [&:not(:first-child)]:before:content-[""] [&:not(:last-child)]:pr-px',
                className
            )}
        >
            {renderToolbarChildren(props.children, {
                disabled,
                size,
            })}
        </div>
    );
}

/** Render toolbar children while preserving inherited props across React fragments. */
function renderToolbarChildren(
    children: React.ReactNode,
    inheritedProps: Partial<ButtonProps>,
    allowToolbarGroups = false
): React.ReactNode {
    return React.Children.map(children, (child) => {
        if (isValidFragment(child)) {
            return renderToolbarChildren(child.props.children, inheritedProps, allowToolbarGroups);
        }

        // Accept `ToolbarGroup` as direct child of `Toolbar`
        if (allowToolbarGroups && isValidToolbarGroup(child)) {
            return React.cloneElement(
                child,
                // Pass down Toolbar `size` to `ToolbarGroup` to override buttons size
                { ...child.props, size: inheritedProps.size }
            );
        }

        return <ToolbarItem child={child} inheritedProps={inheritedProps} />;
    });
}

function ToolbarItem({
    child,
    inheritedProps,
}: {
    child: React.ReactNode;
    inheritedProps: Partial<ButtonProps>;
}) {
    if (isValidButton(child)) {
        return React.cloneElement(
            child,
            getToolbarButtonPropsWithDefaults({
                ...(child.props as object),
                ...inheritedProps,
            } as ButtonProps)
        );
    }

    // Accept composites (ie: wrapper around <Button>) as direct child of `ToolbarGroup`
    if (React.isValidElement<any>(child) && typeof child.type !== 'string') {
        return React.cloneElement(
            child,
            getToolbarButtonPropsWithDefaults({
                ...(child.props as object),
                ...inheritedProps,
            } as ButtonProps)
        );
    }

    return child;
}

/**
 * Utils
 */
export type ToolbarItemProps = Partial<ButtonProps>;
const getToolbarButtonPropsWithDefaults = (buttonProps: ButtonProps) => ({
    kind: buttonProps.kind ?? 'blank',
    size: buttonProps.size ?? 'medium',
    iconOnly: buttonProps.iconOnly ?? true,
    disabled: buttonProps.disabled ?? false,
    preventFocusOnPress: true,
});

function isValidButton(child: React.ReactNode): child is React.ReactElement<ButtonProps> {
    return React.isValidElement(child) && child.type === Button;
}

function isValidFragment(
    child: React.ReactNode
): child is React.ReactElement<{ children?: React.ReactNode }> {
    return React.isValidElement(child) && child.type === React.Fragment;
}

function isValidToolbarGroup(
    child: React.ReactNode
): child is React.ReactElement<ToolbarGroupProps> {
    return React.isValidElement(child) && child.type === ToolbarGroup;
}
